// OdemeBasarili.js
import React, { useEffect, useState } from "react";
import { useSearchParams, Link  } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Alert
} from "reactstrap";
import { checkPaymentFallback, markPaymentFallback } from "../../../api/requests/payment-requests";

const OdemeBasarili = () => {
    const [searchParams] = useSearchParams();
    const [merchantOid, setMerchantOid] = useState("");
    const [isAlreadyUsed, setIsAlreadyUsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isInvalidMerchantOid, setIsInvalidMerchantOid] = useState(false);
    const [copied, setCopied] = useState(false);

    const [paymentDetails, setPaymentDetails] = useState(null);

    useEffect(() => {
        const oid = searchParams.get("merchant_oid");
        if (oid) {
            setMerchantOid(oid);
            checkUrlStatus(oid);
        } else {
            setLoading(false);
            setIsInvalidMerchantOid(true);
        }
        document.title = "Ödeme Başarılı - Tahsilat360";
    }, [searchParams]);

    useEffect(() => {
        const id = "payment-success-mobile-styles";
        if (!document.getElementById(id)) {
            const style = document.createElement("style");
            style.id = id;
            style.innerHTML = `
:root { --ps-safe-top: env(safe-area-inset-top, 16px); }
.payment-success-wrapper { padding-top: var(--ps-safe-top); }
@media (max-width: 576px){
  .payment-success-card .card-body { padding: 1.25rem !important; padding-top: 4.5rem !important; }
  .payment-success-icon { width: 72px !important; height: 72px !important; margin-top: 36px !important; }
  .payment-success-icon i { font-size: 36px !important; }
  .status-badge { font-size: 14px !important; padding: 0.5rem 0.75rem !important; }
}
            `;
            document.head.appendChild(style);
        }
    }, []);

    const checkUrlStatus = async (merchantOid) => {
        try {
            setLoading(true);

            const checkResponse = await checkPaymentFallback(merchantOid);
            const checkData = await checkResponse.json();

            // API'den data objesi gelmezse veya hata varsa geçersiz say
            if (!checkResponse.ok || !checkData.data) {
                setIsInvalidMerchantOid(true);
                return;
            }

            // Gelen tüm ödeme detaylarını state'e kaydet
            setPaymentDetails(checkData.data);

            // 'fallback' kontrolünü data objesi üzerinden yap
            if (checkData.data.fallback === true) {
                setIsAlreadyUsed(true);
            } else {
                // URL ilk defa kullanılıyor, fallback olarak işaretle
                const markResponse = await markPaymentFallback(merchantOid);

                if (markResponse.ok) {
                    setIsAlreadyUsed(false);
                } else {
                    console.error('Fallback işaretleme hatası');
                    setIsInvalidMerchantOid(true);
                }
            }
        } catch (error) {
            console.error('URL durumu kontrol hatası:', error);
            setIsInvalidMerchantOid(true);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Yardımcı formatlama fonksiyonları
    const formatAmount = (amount) => {
        if (amount === null || typeof amount === 'undefined') return "-";
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) {
            return new Date().toLocaleDateString("tr-TR", {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
        }
        return new Date(dateString).toLocaleDateString("tr-TR", {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    // Loading durumu
    if (loading) {
        return (
            <div
                className="min-vh-100 d-flex align-items-center justify-content-center payment-success-wrapper"
                style={{
                    background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                    paddingTop: "env(safe-area-inset-top)"
                }}
            >
                <div className="text-center text-white">
                    <div className="spinner-border mb-3" role="status">
                        <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                    <p>Ödeme durumu kontrol ediliyor...</p>
                </div>
            </div>
        );
    }

    // Geçersiz merchant OID durumu
    if (isInvalidMerchantOid) {
        return (
            <div
                className="min-vh-100 d-flex align-items-center justify-content-center payment-success-wrapper"
                style={{
                    background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                    paddingTop: "env(safe-area-inset-top)"
                }}
            >
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={6} md={8} sm={10}>
                            <Card className="border-0 shadow-lg" style={{ borderRadius: "20px" }}>
                                <CardBody className="p-5 text-center">
                                    <Alert color="danger" className="border-0 mb-4">
                                        <h4 className="alert-heading">Hata!</h4>
                                        <p className="mb-0">Geçersiz işlem numarası veya ödeme bulunamadı.</p>
                                    </Alert>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div
            className="min-vh-100 d-flex align-items-center justify-content-center payment-success-wrapper"
            style={{
                background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
            }}
        >
            <Container className="px-3 px-sm-0">
                <Row className="justify-content-center">
                    <Col lg={6} md={8} sm={10}>
                        <Card
                            className="border-0 shadow-lg payment-success-card"
                            style={{
                                borderRadius: "20px",
                                overflow: "hidden"
                            }}
                        >
                            <CardBody className="text-center p-4 p-sm-5">
                                {/* Success Icon */}
                                <div
                                    className="d-flex align-items-center justify-content-center mb-4 mx-auto payment-success-icon mt-4 mt-sm-0"
                                    style={{
                                        width: "100px",
                                        height: "100px",
                                        background: isAlreadyUsed
                                            ? "linear-gradient(135deg, #ff9800, #f57c00)"
                                            : "linear-gradient(135deg, #4CAF50, #45a049)",
                                        borderRadius: "50%",
                                        boxShadow: isAlreadyUsed
                                            ? "0 10px 30px rgba(255, 152, 0, 0.3)"
                                            : "0 10px 30px rgba(76, 175, 80, 0.3)"
                                    }}
                                >
                                    <i
                                        className={`bx ${isAlreadyUsed ? 'bx-info-circle' : 'bx-check'} text-white`}
                                        style={{ fontSize: "48px" }}
                                    ></i>
                                </div>

                                {/* Success/Warning Alert */}
                                <Alert
                                    color={isAlreadyUsed ? "warning" : "success"}
                                    className="border-0 mb-4 mt-4 mt-sm-0"
                                >
                                    <div className="d-flex align-items-center justify-content-center">
                                        <i
                                            className={`bx ${isAlreadyUsed ? 'bx-info-circle' : 'bx-check-circle'} me-2`}
                                            style={{ fontSize: "24px" }}
                                        ></i>
                                        <div>
                                            <h4 className="alert-heading mb-1">
                                                {isAlreadyUsed ? "URL Daha Önce Kullanılmış!" : "Ödeme Başarılı!"}
                                            </h4>
                                            <p className="mb-0">
                                                {isAlreadyUsed
                                                    ? "Bu başarı sayfası daha önce görüntülenmiş"
                                                    : "İşleminiz başarıyla tamamlandı"
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </Alert>

                                {/* Description */}
                                <div className="mb-4">
                                    <p
                                        className="text-muted mb-0"
                                        style={{
                                            fontSize: "16px",
                                            lineHeight: "1.6"
                                        }}
                                    >
                                        {isAlreadyUsed
                                            ? "Bu başarı sayfası daha önce görüntülenmiş olduğu için tekrar yönlendirme yapılmamıştır. Ödeme durumunuz için lütfen destek ekibiyle iletişime geçin."
                                            : "Ödemeniz başarıyla işleme alınmış ve kaydedilmiştir. Teşekkür ederiz!"
                                        }
                                    </p>
                                </div>

                                {/* Transaction Details Card */}
                                <Card className="border-0 bg-light mb-4">
                                    <CardBody className="p-4">
                                        {/* Başlık */}
                                        <div className="mb-4 text-center">
                                            <h6 className="text-muted mb-0">
                                                <i className="bx bx-receipt me-2 align-middle"></i>
                                                İŞLEM DETAYLARI
                                            </h6>
                                        </div>

                                        {/* İşlem Numarası */}
                                        <div className="mb-4 text-center">
                                            <label className="form-label text-muted small mb-1">İşlem Numarası</label>
                                            <div className="d-flex align-items-center justify-content-center">
                                                <Badge
                                                    color={isAlreadyUsed ? "warning" : "success"}
                                                    className="px-3 py-2 me-2"
                                                    style={{ fontSize: "14px", fontFamily: "'Courier New', monospace" }}
                                                >
                                                    {merchantOid || "Bilinmiyor"}
                                                </Badge>
                                                {merchantOid && (
                                                    <Button
                                                        color={copied ? "info" : (isAlreadyUsed ? "outline-warning" : "outline-success")}
                                                        size="sm"
                                                        onClick={() => copyToClipboard(merchantOid)}
                                                        title="İşlem Numarasını Kopyala"
                                                        className="transition-all"
                                                    >
                                                        <i className={`bx ${copied ? 'bx-check-double' : 'bx-copy'}`}></i>
                                                    </Button>
                                                )}
                                            </div>
                                            <div style={{ height: '20px' }} className="mt-1">
                                                {copied && (
                                                    <small className="text-info fw-bold animate__animated animate__fadeInUp">
                                                        İşlem numarası kopyalandı!
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tutar, Taksit ve Tarih Bilgileri */}
                                        {paymentDetails && (
                                            <Row className="text-center border-top border-bottom py-3">
                                                {/* Ödenen Tutar */}
                                                <Col xs={4} className="border-end">
                                                    <label className="form-label text-muted small mb-1 d-block">Tutar</label>
                                                    <div className="h5 text-success mb-0">
                                                        {formatAmount(paymentDetails.amount)}
                                                    </div>
                                                </Col>

                                                {/* Taksit */}
                                                <Col xs={4} className="border-end">
                                                    <label className="form-label text-muted small mb-1 d-block">Taksit</label>
                                                    <div className="text-dark fw-medium" style={{ fontSize: '1.1rem', paddingTop: '0.3rem' }}>
                                                        {paymentDetails.installmentCount > 1
                                                            ? `${paymentDetails.installmentCount}`
                                                            : "Tek Çekim"}
                                                    </div>
                                                </Col>

                                                {/* İşlem Tarihi */}
                                                <Col xs={4}>
                                                    <label className="form-label text-muted small mb-1 d-block">Tarih</label>
                                                    <div className="text-dark" style={{ fontSize: '0.9rem', paddingTop: '0.4rem' }}>
                                                        {formatDate(paymentDetails?.paymentDate)}
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>

                                {/* Status Badge */}
                                <div className="mb-4">
                                    <Badge
                                        color={isAlreadyUsed ? "warning" : "success"}
                                        className="px-4 py-3 status-badge"
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: "600",
                                            borderRadius: "25px"
                                        }}
                                    >
                                        <i className={`bx ${isAlreadyUsed ? 'bx-info-circle' : 'bx-check-circle'} me-2`}></i>
                                        {isAlreadyUsed ? "URL Daha Önce Kullanılmış" : "Ödeme Tamamlandı"}
                                    </Badge>
                                </div>

                                <div className="text-center mb-4">
                                    <Link to="/payment-history">
                                        <Button
                                            color="primary"
                                            className="w-100 w-sm-auto px-4 py-2"
                                            style={{ fontWeight: "600" }}
                                        >
                                            <i className="bx bx-history me-2"></i>
                                            Ödeme Geçmişine Git
                                        </Button>
                                    </Link>
                                </div>


                                {/* Additional Info for Already Paid */}
                                {isAlreadyUsed && (
                                    <Alert color="info" className="border-0 mb-4">
                                        <small>
                                            <i className="bx bx-info-circle me-2"></i>
                                            Bu başarı sayfası URL'i daha önce kullanılmış ve geçersiz kılınmıştır.
                                            Ödeme durumunuz için destek ekibiyle iletişime geçebilirsiniz.
                                        </small>
                                    </Alert>
                                )}

                                {/* Footer Info */}
                                <div className="mt-4 pt-4 border-top">
                                    <p className="text-muted small mb-0">
                                        <i className="bx bx-shield-check me-2"></i>
                                        Bu işlem güvenli SSL bağlantısı ile gerçekleştirilmiştir
                                    </p>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default OdemeBasarili;
